import { useCallback, useState } from "react";
import { Link } from "react-router";
import type { ScheduleEntry } from "@edu/shared";
import { BookingCancel } from "./BookingCancel.tsx";
import { EntryAction } from "./EntryAction.tsx";
import { EntryCall } from "./EntryCall.tsx";
import { EntryHomework } from "./EntryHomework.tsx";
import { EntryKind } from "./EntryKind.tsx";
import { EntryMarks } from "./EntryMarks.tsx";
import { EntryNotes } from "./EntryNotes.tsx";
import { EntryTools } from "./EntryTools.tsx";
import { EntryWords } from "./EntryWords.tsx";
import { membersHiddenNote } from "./group-text.ts";
import { CloseIcon } from "./icons.tsx";
import { STATE_LABEL, STATE_TONE, errorText, firstLetter, timeText } from "./common.ts";

/**
 * Карточка занятия: всё, что про него написано, и всё, что с ним можно сделать.
 *
 * Раньше она была строкой списка по дням. С переходом на календарь колонками
 * карточка отвязана от раскладки: в колонке стоит только коробка со временем и
 * именем, а карточка раскрывается из самого занятия (`EntryPopover`), на узком
 * экране снизу. Причина геометрическая: занятие на 45 минут это коробка
 * высотой в три строки, и впихнуть в неё причину отмены, пометку о переводе
 * часов, два предупреждения о наложениях и форму переноса нельзя никак.
 *
 * ⚠️ Занятия здесь ТРИ ВИДА, и они ведут себя по-разному:
 *
 * 1. **занятие серии** (`source: "series"`): отменяется и переносится
 *    исключением, ключ это `(slotId, occursOn)`
 * 2. **разовое занятие** (`standalone`): урок со временем и без серии.
 *    Отменяется корзиной самого урока, переносится сменой его времени
 * 3. **осиротевший урок** (`source: "lesson"`, не `standalone`): расписание
 *    поправили после того, как урок прошёл. Состояния у него нет вовсе, и
 *    делать с ним из расписания нечего: он просто виден на своём месте
 *
 * ⚠️ **Ряд действий один, и он внизу карточки.** Порядок в нём такой:
 * значки-инструменты слева, главное действие («Войти», «Подготовить»,
 * «Разбор») прижато вправо. Значки живут в `EntryTools.tsx`, там же разобрано,
 * почему инструменты это фигуры, а главное действие это слово. Раскрытые формы
 * уезжают на свою строку тем же рядом, порядком в стилях (`order`), а не
 * вторым контейнером.
 *
 * ⚠️ **Переходы в соседние работы ссылками, а не кнопками, и в ряд действий они
 * не входят.** Их сегодня два: имя того, с кем занятие (`CounterpartName` ниже),
 * и «задать домашнюю» (`EntryHomework.tsx`). Оба это уход в чужой раздел, а не
 * действие над занятием, и в ряду они либо оттеснили бы «Войти», либо встали бы
 * рядом с «Отменить» на равных.
 *
 * ⚠️ **Карточка это теперь сборка из пяти кусков** (03.09.2026, пределы в 400
 * строк на файл и 150 на функцию, раздел 9 CLAUDE.md). Здесь остались шапка
 * (время, имя, состояние), общая обвязка действий и ряд кнопок внизу, а
 * остальное живёт рядом: `EntryNotes.tsx` объясняет словами, что с занятием не
 * так; `EntryMarks.tsx` пишет отметки; `EntryHomework.tsx` уводит в домашку;
 * `EntryCall.tsx` держит внешний созвон; `EntryAction.tsx` рисует главное
 * действие. Граница у всех одна: карточка ОПИСЫВАЕТ занятие, а они с ним
 * что-то делают, и у каждого своё состояние, которое соседям не нужно.
 *
 * ⚠️ **Акцентной кнопки в карточке нет НИ ОДНОЙ, и это правило, а не пропуск.**
 * Карточка описывает одно занятие из недели, а занятий на экране десятки:
 * двенадцать залитых «Войти» это ноль выделенных кнопок. Второй довод сильнее
 * первого и держится даже сейчас, когда карточка раскрывается по одной: тут же
 * рядом живёт отмена занятия, а красное и синее в одном окне не встречаются.
 * Главное действие раздела задаётся не здесь, а самим календарём.
 */
export function EntryCard({
  entry,
  zone,
  onChanged,
  onClose,
  chat,
}: {
  entry: ScheduleEntry;
  zone: string;
  onChanged: () => Promise<void>;
  /** Закрыть карточку. Кнопка нужна пальцу: Escape на телефоне нечем нажать. */
  onClose?: () => void;
  /**
   * Чат занятия готовым узлом, либо ничего.
   *
   * ⚠️ **Узлом, а не пропсами чата, и это не лень.** Набранное и не отправленное
   * обязано пережить закрытие карточки (карточка закрывается на любое нажатие
   * мимо и на Escape), поэтому лента и черновик живут ВЫШЕ, у того, кто держит
   * выбранное занятие. Приняв сюда сам хук, карточка утащила бы его в своё
   * поддерево и уносила бы черновик вместе с собой. Раздел 12
   * `docs/room-chat-design.md` требует этого дословно.
   */
  chat?: React.ReactNode;
}) {
  const state = entry.state ?? "detached";
  const label = STATE_LABEL[state];
  const dim = entry.state === "cancelled" || entry.state === "moved_away";
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Почему в комнату не пускает, если не пускает. Правило целиком в `group-text.ts`. */
  const hidden = membersHiddenNote({
    roomAvailable: entry.roomAvailable,
    membersVisible: entry.group?.membersVisible,
    state: entry.state,
  });
  /** Общая обвязка действий: занятость, понятный отказ, перечитывание недели. */
  const run = useCallback(
    async (fn: () => Promise<void>) => {
      setBusy(true);
      setErr(null);
      try {
        await fn();
        await onChanged();
      } catch (e) {
        setErr(errorText(e));
      } finally {
        setBusy(false);
      }
    },
    [onChanged],
  );

  return (
    <div className="card wk" data-dim={dim}>
      <div className="wk__row">
        <span className="wk__time">{timeText(entry.at, zone)}</span>
        <span className="wk__who" aria-hidden="true">
          {firstLetter(entry.counterpartName)}
        </span>
        <div className="wk__main">
          <div className="card__title">
            {/*
              Вид занятия стоит В СТРОКЕ ИМЕНИ и перед ним, а не отдельной
              строкой ниже: он про то же самое, про кого это занятие, и своей
              строкой повторял бы вопрос, на который имя уже ответило. Та же
              фигура и в том же порядке стоит в коробке сетки, поэтому
              раскрытая карточка не переучивает глаз
            */}
            <EntryKind entry={entry} />
            {/*
              Зачёркивание отменённого занятия висит на САМОМ ИМЕНИ, а не на
              всей строке заголовка (правка по итогам ревью 30.08.2026). На
              строке оно проходило по значку вида занятия и по числу
              участников, то есть перечёркивало факт «занятие групповое»,
              который отмена не отменяет. Снять его у потомка правилом нечем:
              `text-decoration` наследуется и потомком не отключается
            */}
            <span className="wk__name" data-strike={entry.state === "cancelled"}>
              <CounterpartName entry={entry} />
            </span>
            {/*
              Пилюля состояния стоит В СТРОКЕ ИМЕНИ, а не отдельной колонкой
              справа: своей колонкой она вместе с крестиком не помещалась в
              23rem карточки и уезжала на вторую строку, унося крестик в
              середину карточки. Разбор арифметики в стилях
            */}
            <span className="pill" data-tone={STATE_TONE[state]}>
              {label}
            </span>
          </div>
          <p className="muted">
            {entry.title ?? "Занятие"}
            {entry.durationMin !== null && ` · ${String(entry.durationMin)} мин`}
            {entry.standalone && " · разовое"}
            {entry.myRole === "student" && " · ваш преподаватель"}
            {/* Урок из записи на открытое время (`docs/booking-design.md`, 1.8):
                так преподаватель узнаёт, что занятие поставил не он */}
            {entry.selfBooked === true && " · ученик записался сам"}
          </p>
        </div>
        {onClose !== undefined && (
          // Крестик это хром: он ничего не делает с занятием, он убирает
          // карточку. Поэтому он стоит в шапке, а не в ряду действий внизу:
          // рядом с «Войти» он читался бы третьим действием над занятием
          <button
            type="button"
            className="btn btn--ico btn--quiet wk__close"
            onClick={onClose}
            title="Закрыть карточку"
            aria-label="Закрыть карточку"
          >
            <CloseIcon />
          </button>
        )}
      </div>

      <EntryNotes entry={entry} zone={zone} />

      {/*
        Учёт прямо в карточке занятия: отметить оплату и посещение можно, не
        уходя из расписания. Это и был смысл просьбы «должно быть видно сразу в
        расписании»: карточка ученика отвечает на вопрос «сколько всего», а
        расписание на вопрос «за это заплатили».

        ⚠️ Отметка держится на КЛЮЧЕ ЗАНЯТИЯ, а не на уроке, поэтому работает и
        у занятия, которое уроком не стало: в режиме сосуществования (Zoom по
        внешней ссылке) уроков не создаётся вовсе.

        ⚠️ Своего ключа полосе больше не нужно: карточку целиком пересоздаёт
        ключ занятия в `EntryPopover`, и вместе с ней обнуляется всё набранное и
        сказанное, включая объяснение отказа ручки месяцев
      */}
      <EntryMarks entry={entry} busy={busy} onRun={run} />

      {/* Домашка задаётся ПОСЛЕ занятия, а занятие в этот момент открыто здесь.
          Тихой строкой, а не кнопкой в ряду: разбор в `entry-homework.ts` */}
      <EntryHomework entry={entry} />

      {/* Слова занятия: поле у преподавателя, строка с переходом в тренировку у
          ученика. Кому что, решает сервер полями строки (`EntryWords.tsx`) */}
      <EntryWords entry={entry} />

      {err !== null && <p className="err">{err}</p>}

      <EntryCall entry={entry} dim={dim} busy={busy} run={run} />

      {/*
        Комната у занятия есть, а войти в неё нельзя, пока имена в группе
        скрыты. Текст стоит РЯДОМ с кнопкой и кнопку не отнимает: состав в
        признак `roomAvailable` не входит намеренно, иначе отказ сервера стал бы
        недостижим из интерфейса (раздел 5 docs/group-room-design.md).

        ⚠️ Своей ссылки на карточку группы здесь больше НЕТ (снята 03.09.2026).
        Туда ведёт название группы строкой выше, и второй переход по тому же
        адресу в одной карточке это указание пальцем на соседний элемент.
      */}
      {hidden !== null && <p className="note">{hidden}</p>}

      {/*
        Ряд действий. Инструменты преподавателя приходят фрагментом значков, а
        главное действие прижимается вправо стилями: так у карточки одна строка
        кнопок вместо двух, и «Войти» не соседствует с «Отменить» на равных.

        ⚠️ **Дети стоят каждый на своей строке, и это не форматирование.** Ряд
        бывает пуст целиком (ученик у отменённого занятия), и прячет его тогда
        `.wk__tools:empty`, а `:empty` не совпадает с элементом, внутри которого
        есть текстовый узел хотя бы из одних пробелов. Перевод строки между
        тегами JSX выбрасывает сам, пробел между выражениями в одну строку нет.
        Свести эти две строки в одну значит вернуть лишний отступ молча.
      */}
      <div className="wk__tools">
        <EntryTools entry={entry} zone={zone} busy={busy} run={run} />
        <EntryAction entry={entry} zone={zone} busy={busy} run={run} />
      </div>

      {/* Своя строка под рядом действий: второй шаг раскрывает пояснение, и в
          ряд кнопок оно не встаёт */}
      <BookingCancel entry={entry} busy={busy} run={run} />

      {/*
        Чат стоит ПОСЛЕ ряда действий, и это решение о порядке чтения. Он самый
        высокий блок карточки, а «Войти» и «Разбор» это то, ради чего карточку
        открывают чаще всего: подняв чат выше, мы отправили бы главное действие
        под нижнюю кромку экрана на телефоне.
      */}
      {chat}
    </div>
  );
}

/**
 * Имя того, с кем занятие, и переход на его карточку.
 *
 * ⚠️ **Имя это ссылка, потому что дальше человек идёт именно туда.** Из карточки
 * занятия до 03.09.2026 не вело ни одной двери к ученику, и «посмотреть, что у
 * него с домашкой» стоило ухода в список учеников и поиска имени глазами. Теперь
 * это одно нажатие по имени, которое человек уже читает.
 *
 * ⚠️ **Имя УВОДИТ на карточку, а не открывает окно поверх сетки.** До
 * 14.09.2026 было окно, и сетка оставалась под ним вместе с открытой неделей.
 * Окно снято решением владельца 13.09.2026: один адрес рисовался двумя разными
 * экранами, и у окна не было ни раздела «Где дыры», ни списка занятий, ни
 * анкеты, то есть за ними карточку открывали второй раз. Цена перехода
 * известна и принята: расписание закрывается, и возвращает к нему шаг назад по
 * истории. Разбор в шапке `App.tsx`.
 *
 * ⚠️ **У сводной строки занятия ГРУППЫ ученика нет ни одного**
 * (`counterpartUserId` пустой), и имя там это название группы. Ссылка ведёт на
 * карточку группы, потому что второй стороной занятия выступает именно она, и
 * это тот же адрес, куда ведёт строка про скрытый состав ниже.
 *
 * ⚠️ **Ученику ссылки нет.** `counterpartUserId` у него это преподаватель, а
 * карточка ученика открывается только тому, кто её ведёт: ручка ответила бы 404,
 * а ссылка, ведущая в отказ, хуже отсутствующей.
 */
function CounterpartName({ entry }: { entry: ScheduleEntry }) {
  const name =
    entry.counterpartName ?? (entry.myRole === "student" ? "Преподаватель" : "Без ученика");

  if (entry.myRole === "teacher" && entry.counterpartUserId !== null) {
    return <Link to={`/students/${entry.counterpartUserId}`}>{name}</Link>;
  }
  if (entry.group !== undefined) return <Link to={`/groups/${entry.group.id}`}>{name}</Link>;
  return <>{name}</>;
}
